import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import CustomTable from '../../components/CustomTable'
import FilterModal from '../../components/FilterModal'
import Api from '../../api/endPoints'
import { setVimDevices } from '../../features/vimData/vimSlice'
import { setBreadcrumb } from '../../features/breadcrumb/breadcrumbSlice'
import { selectAssetProfileId, selectAssetProfileStatus, fetchAssetProfile, resetAssetProfile } from '../../features/assetProfile/assetProfileSlice'
import { formatDateTime } from '../../utils/date'
import { flattenOrgOptions } from '../../utils/organizations'

const STATUS_OPTIONS = [
  { value: 'ONLINE', label: 'Online' },
  { value: 'OFFLINE', label: 'Offline' },
]

const StatusBadge = ({ status }) => {
  const isOnline = String(status).toUpperCase() === 'ONLINE'
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${isOnline ? 'bg-[#daf5ed] text-[#006c4d]' : 'bg-[#eceded] text-[#5e6673]'}`}>
      {status}
    </span>
  )
}

// Columns map straight onto the clubbed /nsync/profiles/:id/devices items —
// orgName, activeCount and status now come on each item, so no second call.
const COLUMNS = [
  { key: 'deviceId', label: 'VIM ID', width: '220px', render: (row) => row.deviceId },
  { key: 'online', label: 'Status', width: '120px', render: (row) => <StatusBadge status={row.online ? 'Online' : 'Offline'} /> },
  { key: 'orgName', label: 'Organization', width: '180px', render: (row) => row.orgName ?? '-' },
  { key: 'activeNodes', label: 'Active Nodes', width: '140px', render: (row) => row.activeCount },
  { key: 'lastSeen', label: 'Last Seen at', render: (row) => formatDateTime(row.lastSeen) },
]

const VimList = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const user = useSelector((state) => state.user.user)
  // The org whose linked customers make up the Organization filter. Undefined
  // until /user/me resolves, which is why the fetch effect below waits on it.
  const organizationId = user?.role?.organizationId
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [devicesData, setDevicesData] = useState([])
  // Fetched once per session by Layout — null until it resolves, which is why
  // the fetch effect below waits on it.
  const profileID = useSelector(selectAssetProfileId)
  const profileStatus = useSelector(selectAssetProfileStatus)
  const [orgOptions, setOrgOptions] = useState([])
  const [filters, setFilters] = useState({ status: [], organization: [] })
  const [search, setSearch] = useState('')
  const [showSearch, setShowSearch] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Bumped by the error state's Retry button to re-run the fetch effect.
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    dispatch(setBreadcrumb([{ title: 'VIM List', link: '/vim-list', current: true }]))
  }, [dispatch])


  const orgFilter = filters.organization.length === 1 ? filters.organization[0] : undefined
  const onlineFilter = filters.status.length === 1 ? filters.status[0].toLowerCase() : undefined

  // max/totalPages come straight from the backend (real offset/limit
  // pagination) — no need to estimate or derive them locally.
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  // Every filter/sort/page change re-runs this, so overlapping requests are
  // routine: `cancelled` keeps a superseded response from overwriting fresher
  // rows or clearing the loading flag while the newer request is still open.
  useEffect(()=>{
    let cancelled = false

    // No profile id means no query to make. A failed profile load has to
    // surface as an error, otherwise the table would sit on its spinner
    // forever with nothing explaining why.
    if (profileID == null) {
      if (profileStatus === 'failed') {
        setError("Couldn't load VIM devices")
        setIsLoading(false)
      } else {
        setIsLoading(true)
      }
      return
    }

    const fetchVimDevices = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const response = await Api.getProfileDevices(profileID, orgFilter, onlineFilter, currentPage, itemsPerPage)
        if (cancelled) return
        // `request` resolves for every status instead of throwing, so an HTTP
        // error never reaches the catch below — without this check a 500 would
        // read as "no devices" rather than as a failure.
        if (response.status !== 200) {
          console.error('VIM devices request failed with status', response.status)
          setError("Couldn't load VIM devices")
          return
        }
        const data = response.data ?? {}
        const items = data.items ?? []
        setDevicesData(items)
        dispatch(setVimDevices(items))
        setTotalItems(data.max ?? 0)
        setTotalPages(data.totalPages ?? 1)
      } catch (error) {
        console.error('Error fetching VIM devices:', error)
        // Without this the table would look identical to a genuinely empty
        // fleet — the one reading an operator must not be given by mistake.
        if (!cancelled) setError("Couldn't load VIM devices")
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    fetchVimDevices()
    return () => { cancelled = true }
  }, [profileID, profileStatus, orgFilter, onlineFilter, currentPage, itemsPerPage, reloadKey, dispatch])

  useEffect(() => {
    if (organizationId == null) return
    let cancelled = false

    const fetchLinkedCustomers = async () => {
      try {
        const { status, data } = await Api.getProfileLinkedCustomers(organizationId)
        if (cancelled) return
        // options show the org name; the id is what gets sent as orgId
        const options = flattenOrgOptions(data?.list ?? data)
        if (status === 200 && options.length > 0) {
          setOrgOptions(options)
        }
      } catch (error) {
        console.error('Failed to fetch linked customers:', error)
      }
    }

    fetchLinkedCustomers()
    return () => { cancelled = true }
  }, [organizationId])

  // The profile id is what the device query is built from, so when *it* is what
  // failed, bumping reloadKey alone would retry nothing — reset it back to idle
  // so the thunk's once-per-session guard lets a fresh attempt through.
  const handleRetry = () => {
    if (profileStatus === 'failed') {
      dispatch(resetAssetProfile())
      dispatch(fetchAssetProfile())
    }
    setReloadKey((n) => n + 1)
  }

  const handleDownloadCsv = () => {
    const headers = ['VIM ID', 'Status', 'Organization', 'Active Nodes', 'Last Seen at']
    const escapeCell = (value) => {
      const text = String(value ?? '')
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
    }
    const rows = filteredDevices.map((row) => [
      row.deviceId,
      row.online ? 'ONLINE' : 'OFFLINE',
      row.orgName ?? '-',
      row.activeCount,
      formatDateTime(row.lastSeen),
    ])
    const csv = [headers, ...rows].map((cells) => cells.map(escapeCell).join(',')).join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'vim-device-list.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  const filteredDevices = devicesData.filter((row) => {
    if (search.trim()) {
      const query = search.trim().toLowerCase()
      const matchesVimId = String(row.deviceId ?? '').toLowerCase().includes(query)
      if (!matchesVimId) return false
    }
    return true
  })

  // Sorting AND pagination are both handled by the backend now — the list
  // arrives as exactly one page already, so this only search-filters it.
  const pagedDevices = filteredDevices

  return (
    <div className="bg-[#f9fafa] flex flex-col items-start overflow-hidden rounded h-full w-full">
      <div className="flex items-center gap-4 px-4 py-2.5 w-full shrink-0">
        <span className="flex-1 text-sm font-semibold text-[#252a34]">
          VIM Device List
        </span>
        {showSearch ? (
          <div className="flex items-center gap-2 h-9 px-2 rounded border border-[#d8dde6] bg-white w-[227px]">
            <span className="material-icons text-xl text-[#949cac]">search</span>
            <input
              autoFocus
              className="flex-1 min-w-0 outline-none border-none text-sm text-[#252a34] placeholder:text-[#949cac] bg-transparent"
              placeholder="Search VIM ID"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setCurrentPage(1)
              }}
            />
            <button
              className="border-none bg-transparent cursor-pointer flex items-center justify-center p-0"
              onClick={() => {
                setSearch('')
                setShowSearch(false)
                setCurrentPage(1)
              }}
            >
              <span className="material-icons text-base text-[#949cac]">close</span>
            </button>
          </div>
        ) : (
          <button
            className="w-10 h-10 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-full hover:bg-[#eceded]"
            onClick={() => setShowSearch(true)}
          >
            <span className="material-icons text-xl text-[#272d37]">search</span>
          </button>
        )}
        <FilterModal
          tabs={[
            {
              key: "status",
              label: "Status",
              type: "static",
              options: STATUS_OPTIONS,
            },
            {
              key: "organization",
              label: "Organization",
              type: "searchable",
              options: orgOptions,
            },
          ]}
          value={filters}
          onApply={(next) => {
            setFilters(next);
            setCurrentPage(1);
          }}
        />
        <button
          onClick={handleDownloadCsv}
          className="w-10 h-10 p-2 border-none bg-transparent cursor-pointer flex items-center justify-center rounded-full hover:bg-[#eceded]"
        >
          <span className="material-symbols-outlined text-xl text-[#272d37]">
            file_save
          </span>
        </button>
      </div>

      <CustomTable
        columns={COLUMNS}
        data={pagedDevices}
        isLoading={isLoading}
        error={error}
        onRetry={handleRetry}
        loadingLabel="Loading VIM devices…"
        emptyMessage={search.trim() ? 'No VIM devices match this search' : 'No VIM devices found'}
        onRowClick={(row) => navigate(`/ViewDashboard/${row.deviceId}`)}
        pagination={{
          currentPage,
          totalPages,
          totalItems,
          itemsPerPage,
          onPageChange: setCurrentPage,
          onItemsPerPageChange: setItemsPerPage,
        }}
      />
    </div>
  );
}

export default VimList
